import { css } from '@emotion/react'
import * as scale from 'd3-scale'
import { endOfMonth, parseISO } from 'date-fns'
import dynamic from 'next/dynamic'
import { colors } from 'plume-ui'
import * as React from 'react'
import * as ReactDOMServer from 'react-dom/server'
import { formatDate } from '@/lib/date'
import { formatWithComma, thousandToMillion, formatAmount } from '@/lib/number'
import { assertNever } from '@/lib/ts-utils'
import {
  InitialSeries,
  ChartFundingRoundDesc,
  ChartFundingRoundData,
  EmployeesShiftData,
} from '../../../../types/api'
import { CustomTooltip, TooltipDesc } from '../chart/CustomTooltip'
import { employeesShiftTableRecordId } from './EmployeesShiftTable'

const Chart = dynamic(() => import('react-apexcharts'), { ssr: false })

type Props = {
  fundingRounds: ChartFundingRoundData[]
  employeesShift: EmployeesShiftData[]
  endDate: Date
}

export const FundingRoundAndEmployeeTransitionChart: React.FC<Props> = (
  props
) => {
  const markers = markersOfFundingRounds(props.fundingRounds)
  const initialSeriesAnnotations = annotationsOfFundingRounds(
    props.fundingRounds
  )
  const hasEmployeesData = props.employeesShift.length > 0
  const employeesShiftMaxByDate = filterMaxCountByDate(props.employeesShift)

  const fundingRoundsOptions = {
    stroke: { curve: 'stepline', dashArray: 0 },
    yaxis: {
      decimalsInFloat: 0,
      seriesName: '総調達額（百万円）',
      title: {
        text: '総調達額（百万円）',
        rotate: 0,
        offsetX: 65,
        offsetY: -185,
        style: {
          fontSize: '12px',
          fontFamily: 'Helvetica, Arial, sans-serif',
          fontWeight: 300,
        },
      },
      labels: {
        formatter: (v: number) => formatWithComma(thousandToMillion(v)),
      },
      axisBorder: {
        offsetX: 5,
      },
    },
    markers: {
      size: 1,
      colors: undefined,
    },
    tooltip: {
      custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
        return tooltipHTMLOfFundingRounds(props.fundingRounds, dataPointIndex)
      },
    },
  }
  const employeesOptions = {
    stroke: { curve: 'straight', dashArray: 3 },
    yaxis: {
      show: hasEmployeesData,
      opposite: true,
      seriesName: '従業員数（人）',
      title: {
        text: '従業員数（人）',
        rotate: 0,
        offsetX: -40,
        offsetY: -185,
        style: {
          fontSize: '12px',
          fontFamily: 'Helvetica, Arial, sans-serif',
          fontWeight: 300,
        },
      },
      axisBorder: {
        show: false,
      },
    },
    markers: {
      size: 5,
      colors: colorOfRound('従業員数'),
    },
    tooltip: {
      custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
        return tooltipHTMLOfEmployees(employeesShiftMaxByDate, dataPointIndex)
      },
    },
  }
  // see https://apexcharts.com/docs/options/
  const options = {
    chart: {
      type: 'line',
      zoom: { enabled: false },
      toolbar: { show: false },
      events: {
        markerClick: (
          _event: any,
          _chartContext: any,
          {
            dataPointIndex,
            seriesIndex,
          }: { dataPointIndex: number; seriesIndex: number }
        ) => {
          const isFundingRoundMarker = seriesIndex === 0
          const elm = isFundingRoundMarker
            ? document.getElementById(
                props.fundingRounds[dataPointIndex].shortname
              )
            : document.getElementById(
                employeesShiftTableRecordId(
                  employeesShiftMaxByDate[dataPointIndex]
                )
              )
          if (elm) {
            const NAVBAR_HEIGHT = 215
            const top =
              elm.getBoundingClientRect().top + window.scrollY - NAVBAR_HEIGHT
            window.scroll({ top: top, behavior: 'smooth' })
          }
        },
      },
    },
    stroke: {
      curve: [fundingRoundsOptions.stroke.curve, employeesOptions.stroke.curve],
      width: 2,
      lineCap: 'round',
      dashArray: [
        fundingRoundsOptions.stroke.dashArray,
        employeesOptions.stroke.dashArray,
      ],
    },
    colors: [colors.black10],
    xaxis: {
      type: 'datetime',
      labels: {
        datetimeFormatter: {
          year: 'yyyy',
          // Show only year value
          month: '',
        },
      },
      axisBorder: {
        show: true,
        color: colors.black10,
        height: 2,
      },
      axisTicks: {
        show: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    yaxis: [fundingRoundsOptions.yaxis, employeesOptions.yaxis],
    markers: {
      size: [fundingRoundsOptions.markers.size, employeesOptions.markers.size],
      strokeWidth: 0,
      hover: {
        size: undefined,
      },
      colors: [
        fundingRoundsOptions.markers.colors,
        employeesOptions.markers.colors,
      ],
      discrete: markers,
    },
    tooltip: {
      custom: [
        fundingRoundsOptions.tooltip.custom,
        employeesOptions.tooltip.custom,
      ],
    },
    annotations: {
      position: 'back',
      xaxis: initialSeriesAnnotations,
    },
    legend: {
      show: false,
    },
  }

  const series = [
    {
      text: '総調達額（百万円）',
      data: seriesOfFundingRounds(props.fundingRounds, props.endDate),
    },
    {
      name: '従業員数（人）',
      data: seriesOfEmployees(employeesShiftMaxByDate),
    },
  ]

  return (
    <div
      css={css`
        .apexcharts-series-markers {
          mix-blend-mode: multiply;
        }
      `}
    >
      <Chart options={options} series={series} width={1060} height={400} />
    </div>
  )
}

type DataPointMarker = {
  seriesIndex: number
  dataPointIndex: number
  size: number
  fillColor?: string
}

const markersOfFundingRounds = (
  rounds: ChartFundingRoundData[]
): DataPointMarker[] => {
  const markers = rounds.map((round, i) => ({
    seriesIndex: 0,
    dataPointIndex: i,
    size: sizeOfRound(round),
    fillColor: colorOfRound(round.description),
  }))

  if (markers.length === 0) {
    return []
  }

  // 最後のdatapointはチャートの終端を表すために配置している実データのないもののため非表示のマーカーを当てる
  const lastMarker = {
    seriesIndex: 0,
    dataPointIndex: markers.length,
    size: 0,
  }
  return [...markers, lastMarker]
}

const seriesOfFundingRounds = (
  rounds: ChartFundingRoundData[],
  endDate: Date
) => {
  const data = rounds.map((r) => [r.date, r.total])
  if (data.length === 0) {
    return []
  }
  const lastAmount = data[data.length - 1][1]
  const lastItem = [endDate, lastAmount]
  return [...data, lastItem]
}

const seriesOfEmployees = (employeesShift: EmployeesShiftData[]) => {
  return employeesShift.map((data) => {
    const [yyyy, MM, dd] = data.eventDate.includes('/')
      ? data.eventDate.split('/')
      : data.eventDate.split('-')
    const eventDate = dd
      ? data.eventDate
      : formatDate(
          endOfMonth(new Date(`${yyyy}/${MM || '1'}/1`)).toISOString(),
          'yyyy/MM/dd'
        )
    return [eventDate, data.count]
  })
}

const filterMaxCountByDate = (employeesShift: EmployeesShiftData[]) => {
  return employeesShift.reduce((acc, current) => {
    const existing = acc.find((item) => item.eventDate === current.eventDate)
    if (!existing || current.count > existing.count) {
      acc.push(current)
    }
    return acc
  }, [] as EmployeesShiftData[])
}

type DataAnnotation = {
  x: number
  x2: number
  fillColor: string
  opacity: number
  borderColor: string
  label: { text: string } & Object
}
const initialSeriesList: InitialSeries[] = [
  'シード',
  'シリーズA',
  'シリーズB',
  'シリーズC',
  'シリーズD',
  'シリーズE',
  'シリーズF',
  'シリーズG',
]

const annotationsOfFundingRounds = (
  rounds: ChartFundingRoundData[]
): DataAnnotation[] => {
  const roudnsWithSeries = initialSeriesList
    .map((series) => rounds.find((r) => r.initialSeries === series))
    .filter((i) => i !== undefined) as Array<
    ChartFundingRoundData & { initialSeries: string }
  >

  return roudnsWithSeries.map((r, i, arr) => {
    const x2Date = arr[i + 1] ? parseISO(arr[i + 1].date) : new Date()
    return {
      x: parseISO(r.date).getTime(),
      x2: x2Date.getTime(),
      fillColor: colorOfInitialSeries(r.initialSeries),
      borderColor: 'transparent',
      opacity: 0.7,
      label: {
        text: r.initialSeries,
        orientation: 'horizontal',
        borderWidth: 0,
        offsetY: 10,
        offsetX: 30,
        style: {
          background: 'transparent',
          fontSize: '11px',
        },
      },
    }
  })
}

export const colorOfInitialSeries = (s: InitialSeries): string => {
  switch (s) {
    case 'シード':
      return colors.gray30
    case 'シリーズA':
      return '#f3f0ee'
    case 'シリーズB':
      return colors.cyan10
    case 'シリーズC':
      return '#d6ecf0'
    case 'シリーズD':
      return '#b9e3ea'
    case 'シリーズE':
      return colors.cyan40
    case 'シリーズF':
      return colors.cyan50
    case 'シリーズG':
      return colors.cyan60
    default:
      return assertNever(s)
  }
}

export const colorOfRound = (desc: ChartFundingRoundDesc): string => {
  switch (desc) {
    case '有償第三者割当':
    case '有償株主割当':
      return '#5ed8e0'
    case '公募増資':
    case '増資':
    case '資本組み入れ':
    case '現金出資・現物出資':
    case 'クラウドファンディング':
      return '#6cdf43'
    case '調達方法不明':
    case '短期借入(Loan)':
    case '長期借入(Debt)':
      return '#2dbaf0'
    case '創業':
    case '会社設立':
    case '合併（M&A,事業継承など）':
      return colors.red60
    case '新株予約権の付与（発行）':
    case '新株引受権付社債の発行':
    case '新株予約権付社債の発行':
    case '転換社債型新株予約権付社債の発行':
    case '転換社債の発行':
    case '転換ローン(Convertible Note)の発行':
    case '転換ローン(Convertible Note)の発行 (日本版)':
    case '新株引受権の発行':
      return '#f1e95b'
    case '新株予約権の権利行使':
    case '新株引受権付社債の権利行使':
    case '新株予約権付社債の権利行使':
    case 'グリーンシート':
    case '転換社債型新株予約権付社債の権利行使':
    case '転換社債の株式転換':
    case '転換ローン(Convertible Note)の株式転換':
    case '転換ローン(Convertible Note)の株式転換 (日本版)':
    case '新株引受権の権利行使':
      return '#dcd221'
    case '株式の分割':
    case '株式の移動':
      return '#ffae00'
    case '不明':
      return colors.blueGray50
    case '従業員数':
      return '#8d46b0'
    default:
      return assertNever(desc)
  }
}

const sizeOfRound = (round: ChartFundingRoundData): number => {
  switch (round.description) {
    case '創業':
    case '会社設立':
      return 4
    case '新株予約権の付与（発行）':
      return stockSize(round.increasedDilutiveShares)
    case '新株予約権の権利行使':
      return stockSize(round.increasedRealStocks)
    default:
      // "!" is aiming to fix incompatible type for the update below.
      // https://github.com/Japan-Venture-Research/penguin/pull/1176
      return amountScale(round.amount || 0)!
  }
}

const stockSize = (n: number | null) => {
  if (n === null || n < 0) {
    return 3
  }
  switch (true) {
    case n < 500:
      return 4
    case n < 1000:
      return 5
    case n < 5000:
      return 6
    case n < 10000:
      return 7
    case n < 50000:
      return 8
    case n < 100000:
      return 9
    default:
      return 10
  }
}

const amountScale = scale
  .scaleLog()
  .domain([5000, 1000000])
  .range([4, 15])
  .clamp(true)

const tooltipHTMLOfFundingRounds = (
  rounds: ChartFundingRoundData[],
  targetIndex: number
) => {
  const roundsOfTargetDate = rounds.filter(
    (r) => r.date === rounds[targetIndex]?.date
  )

  if (roundsOfTargetDate.length === 0) {
    return null
  }

  const date = formatDate(roundsOfTargetDate[0].date, 'yyyy/MM/dd')

  const items = roundsOfTargetDate.map((r) => ({
    title: r.description,
    color: colorOfRound(r.description),
    dict: tooltipDictOfRound(r),
  }))

  return ReactDOMServer.renderToStaticMarkup(
    <CustomTooltip
      date={date}
      items={items}
      css={css`
        background-color: rgba(255, 255, 255, 0.5);
      `}
    />
  )
}

const tooltipDictOfRound = (round: ChartFundingRoundData): TooltipDesc[] => {
  const formattedAmount = formatAmount(round.amount)
  const formattedTotal = formatAmount(round.total)
  switch (round.description) {
    case '創業':
    case '会社設立':
      return [
        {
          label: '調達時点総調達額',
          value: formattedTotal ? formattedTotal.value : '',
          unit: formattedTotal ? formattedTotal.unit : '',
        },
      ]
    case '有償第三者割当':
    case 'クラウドファンディング':
    case '調達方法不明':
    case '有償株主割当':
    case '公募増資':
    case '増資':
    case '資本組み入れ':
    case '現金出資・現物出資':
    case '転換社債の発行':
    case '新株引受権の権利行使':
    case '合併（M&A,事業継承など）':
    case '不明':
      return [
        {
          label: '調達額',
          value: formattedAmount ? formattedAmount.value : '',
          unit: formattedAmount ? formattedAmount.unit : '',
        },
        {
          label: '調達時点総調達額',
          value: formattedTotal ? formattedTotal.value : '',
          unit: formattedTotal ? formattedTotal.unit : '',
        },
      ]
    case '短期借入(Loan)':
    case '長期借入(Debt)':
    case '転換ローン(Convertible Note)の発行':
    case '転換ローン(Convertible Note)の発行 (日本版)':
      return [
        {
          label: '借入額',
          value: formattedAmount ? formattedAmount.value : '',
          unit: formattedAmount ? formattedAmount.unit : '',
        },
        {
          label: '総額',
          value: formattedTotal ? formattedTotal.value : '',
          unit: formattedTotal ? formattedTotal.unit : '',
        },
      ]
    case '新株予約権の付与（発行）':
    case '新株引受権付社債の発行':
    case '新株予約権付社債の発行':
    case '新株引受権の発行':
    case '転換社債型新株予約権付社債の発行':
      return [
        {
          label: '調達額',
          value: formattedAmount ? formattedAmount.value : '',
          unit: formattedAmount ? formattedAmount.unit : '',
        },
        {
          label: '潜在株数',
          value: round.increasedDilutiveShares
            ? `+${formatWithComma(round.increasedDilutiveShares)}`
            : null,
          unit: '株',
        },
        {
          label: '株価',
          value: formatWithComma(round.pricePerShare),
          unit: '円',
        },
      ]
    case '新株予約権の権利行使':
    case '新株引受権付社債の権利行使':
    case '新株予約権付社債の権利行使':
    case 'グリーンシート':
    case '転換社債型新株予約権付社債の権利行使':
    case '転換社債の株式転換':
      return [
        {
          label: '調達額',
          value: formattedAmount ? formattedAmount.value : '',
          unit: formattedAmount ? formattedAmount.unit : '',
        },
        {
          label: '実株数',
          value: round.increasedRealStocks
            ? `+${formatWithComma(round.increasedRealStocks)}`
            : null,
          unit: '株',
        },
        {
          label: '株価',
          value: formatWithComma(round.pricePerShare),
          unit: '円',
        },
      ]
    case '株式の分割':
      return [
        {
          label: '分割比率',
          value: splitRatio(round) || '不明',
        },
      ]
    case '株式の移動':
      return [
        {
          label: '株価',
          value: formatWithComma(round.pricePerShare),
          unit: '円',
        },
      ]
    case '転換ローン(Convertible Note)の株式転換':
    case '転換ローン(Convertible Note)の株式転換 (日本版)':
      return []
    case '従業員数':
      return []
    default:
      return assertNever(round.description)
  }
}

const splitRatio = (r: ChartFundingRoundData): string | null => {
  if (
    r.numberOfSharesBeforeFunding === null ||
    r.numberOfSharesAfterFunding === null
  ) {
    return null
  }
  return `1:${Math.round(
    r.numberOfSharesAfterFunding / r.numberOfSharesBeforeFunding
  )}`
}

const tooltipHTMLOfEmployees = (
  employeesShift: EmployeesShiftData[],
  targetIndex: number
) => {
  const employeesOfTargetDate = employeesShift[targetIndex]

  const date = formatDate(employeesOfTargetDate.eventDate, 'yyyy/MM/dd')

  const items = [
    {
      title: '従業員数',
      color: colorOfRound('従業員数'),
      dict: [
        {
          label: '従業員数',
          value: employeesOfTargetDate.count,
          unit: '人',
        },
        {
          label: '区分',
          value: employeesOfTargetDate.reportType,
          unit: '',
        },
      ],
    },
  ]

  return ReactDOMServer.renderToStaticMarkup(
    <CustomTooltip
      date={`${date}（${employeesOfTargetDate.dateType}）`}
      items={items}
      css={css`
        background-color: rgba(255, 255, 255, 0.5);
      `}
    />
  )
}
